<script>
    import { onMount, onDestroy } from 'svelte';
    import ActiveStudentDateFilter from './ActiveStudentDateFilter.svelte';
    import { getApiBase } from './apiBase.js';
    import {
        filterRecipients,
        selectedRecipients,
        selectVisible,
        toggleSelection as toggleSelectedId,
    } from './recipientSelection.js';

    // State
    let users = []; // students + teachers (User model)
    let dateStudents = [];
    let parents = []; // Parent model records
    let thisMonthParents = []; // Parent model records limited to current month activity
    let inactiveMonthParents = []; // Parents whose students have no classes this month
    let inactiveMonthParentsGrade12OrBelow = []; // Inactive parents with a known school grade up to 12
    let filtered = [];

    let selectedIds = new Set();
    let isLoading = false;
    let isSending = false;
    let errorMessage = '';
    let successMessage = '';


    let searchQuery = '';

    // Filters: users plus parent activity/grade groups
    let activeFilter = 'all';
    let phoneTarget = 'student';


    // SMS form
    let smsMessage = '';
    let customPhone = ''; // Optional override for testing

    let batch = null, batchId = '', recentBatches = [];
    let preparing = false, submissionUnconfirmed = false, preparedPayload = null;
    let pollTimer, statusAbort, statusGeneration = 0, alive = true;
    const savedBatchKey = 'tutory-bulk-sms-batch';
    const apiBase = getApiBase();

    onMount(() => {
        loadRecipients();
        loadRecentBatches();
        batchId = localStorage.getItem(savedBatchKey) || '';
        if (batchId) { submissionUnconfirmed = true; isSending = true; checkBatchStatus(); }
    });

    async function loadRecipients() {
        isLoading = true;
        errorMessage = '';

        const token = localStorage.getItem('jwtToken');

        try {
            const response = await fetch(`${apiBase}/studentplayground/api/users-for-sms-selection/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to load recipients');
            }

            const data = await response.json();
            users = data.users || [];
            parents = data.parents || [];
            thisMonthParents = data.this_month_parents || [];
            inactiveMonthParents = data.inactive_month_parents || [];
            inactiveMonthParentsGrade12OrBelow = data.inactive_month_parents_grade_12_or_below || [];
            applyFilters();
        } catch (error) {
            errorMessage = `Error loading recipients: ${error.message}`;
            console.error('Error loading SMS recipients:', error);
        } finally {
            isLoading = false;
        }
    }

    function setFilter(filter) {
        if (filter !== activeFilter) {
            phoneTarget = ['parents', 'this_month_parents', 'inactive_month_parents', 'inactive_month_parents_grade_12_or_below'].includes(filter) ? 'parent' : 'student';
        }
        if (filter !== activeFilter) dateStudents = [];
        if (filter !== activeFilter) selectedIds = new Set();
        activeFilter = filter;
        applyFilters();
    }

    function updateDateRecipients(event) {
        dateStudents = event.detail;
        selectedIds = new Set();
        applyFilters();
    }

    function normalizePhone(phone) {
        if (!phone) return '';
        return String(phone).trim();
    }

    function canonicalPhone(phone) {
        const raw = normalizePhone(phone);
        if (!raw) return '';
        let digits = raw.replace(/\D/g, '');
        if (digits.startsWith('00')) digits = digits.slice(2);
        return digits;
    }

    function maskPhone(phone) {
        const p = normalizePhone(phone);
        if (!p) return '';
        const digits = p.replace(/\D/g, '');
        if (digits.length <= 4) return p;
        return `${'*'.repeat(Math.max(0, digits.length - 4))}${digits.slice(-4)}`;
    }

    function recipientLabel(r) {
        if (!r) return '';
        const first = (r.first_name || '').trim();
        const last = (r.last_name || '').trim();
        const name = `${first} ${last}`.trim() || (r.username || '').trim() || `ID ${r.id}`;
        if (r.user_type === 'parent' && r.student_name) {
            return `${name} (parent of ${r.student_name})`;
        }
        return name;
    }

    function recipientSearchText(r) {
        const parts = [
            r.first_name,
            r.last_name,
            r.username,
            r.email,
            r.user_type,
            r.student_name,
            normalizePhone(r.phone_number),
        ].filter(Boolean);
        return parts.join(' ').toLowerCase();
    }

    function getBaseListForFilter() {
        if (activeFilter === 'active_date_students' || activeFilter === 'inactive_date_students') return dateStudents.map(user => withPhoneDestination(user, 'user'));
        if (activeFilter === 'parents') return parents.map(p => withPhoneDestination(p, 'parent'));
        if (activeFilter === 'this_month_parents') return thisMonthParents.map(p => withPhoneDestination(p, 'parent'));
        if (activeFilter === 'inactive_month_parents') return inactiveMonthParents.map(p => withPhoneDestination(p, 'parent'));
        if (activeFilter === 'inactive_month_parents_grade_12_or_below') {
            return inactiveMonthParentsGrade12OrBelow.map(p => withPhoneDestination(p, 'parent'));
        }

        // users list
        let list = users.map(u => withPhoneDestination(u, 'user'));
        if (activeFilter === 'students') list = list.filter(u => u.user_type === 'student');
        if (activeFilter === 'teachers') list = list.filter(u => u.user_type === 'teacher');
        return list;
    }

    function withPhoneDestination(recipient, kind) {
        return {
            ...recipient,
            _kind: kind,
            phone_number: phoneTarget === 'student' ? recipient.student_phone_number : recipient.parent_phone_number,

        };
    }

    function applyFilters() {
        const list = getBaseListForFilter();
        filtered = filterRecipients(list, searchQuery, recipientSearchText);
    }

    function toggleSelection(id) {
        selectedIds = toggleSelectedId(selectedIds, id);
    }

    function selectAllVisible() {
        selectedIds = selectVisible(selectedIds, filtered);
    }

    function clearAll() {
        selectedIds = new Set();
    }

    async function loadRecentBatches() {
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/bulk-sms-batches/`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` }, cache: 'no-store',
            });
            if (response.ok && alive) recentBatches = (await response.json()).results || [];
        } catch (_) { /* Sending and status checks show their own connection errors. */ }
    }

    function displayBatch(result) {
        batch = result;
        isSending = result.pending_sends > 0;
        successMessage = `Accepted ${result.successful_sends} SMS for sending. Confirmed delivered: ${result.delivered_sends}.`;
    }

    function scheduleStatusCheck(result) {
        clearTimeout(pollTimer);
        if (result.pending_sends > 0 || result.monitoring) {
            pollTimer = setTimeout(checkBatchStatus, result.pending_sends > 0 ? 1500 : 15000);
        }
    }

    async function checkBatchStatus() {
        if (!batchId || preparing) return;
        clearTimeout(pollTimer);
        statusAbort?.abort(); statusAbort = new AbortController();
        const id = batchId, generation = ++statusGeneration;
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/bulk-sms-batches/${id}/`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` },
                signal: statusAbort.signal, cache: 'no-store',
            });
            if (!alive || generation !== statusGeneration || id !== batchId) return;
            if (response.status === 404 && !preparedPayload) {
                localStorage.removeItem(savedBatchKey); batchId = ''; submissionUnconfirmed = false; isSending = false;
                errorMessage = 'The previous batch is unavailable. Select recipients for a new batch.';
                return;
            }
            if (!response.ok) throw new Error('Batch status is not confirmed. Use Check batch status before sending again.');
            const result = await response.json();
            if (!alive || generation !== statusGeneration || id !== batchId) return;
            displayBatch(result); submissionUnconfirmed = false; errorMessage = '';
            selectedIds = new Set();
            scheduleStatusCheck(result);
        } catch (error) {
            if (alive && generation === statusGeneration && error.name !== 'AbortError') {
                errorMessage = error.message; submissionUnconfirmed = true; isSending = false;
            }
        }
    }

    async function submitPreparedBatch(payload) {
        if (preparing) return;
        preparing = true; isSending = true; submissionUnconfirmed = true;
        errorMessage = ''; successMessage = '';
        clearTimeout(pollTimer); statusAbort?.abort(); statusGeneration++;
        batchId = payload.batch_id; localStorage.setItem(savedBatchKey, batchId);
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/send-bulk-sms/`, {
                method: 'POST', headers: {
                    Authorization: `Bearer ${localStorage.getItem('jwtToken')}`, 'Content-Type': 'application/json',
                }, body: JSON.stringify(payload),
            });
            const result = await response.json();
            if (!alive) return;
            if (!response.ok) {
                if ([400, 401, 403].includes(response.status)) {
                    submissionUnconfirmed = false; localStorage.removeItem(savedBatchKey); batchId = ''; preparedPayload = null;
                }
                throw new Error(result.message || result.detail || 'The batch could not be confirmed. Check its status.');
            }
            displayBatch(result); submissionUnconfirmed = false;
            selectedIds = new Set(); smsMessage = ''; preparedPayload = null;
            loadRecentBatches(); scheduleStatusCheck(result);
        } catch (error) {
            if (alive) {
                isSending = false;
                errorMessage = error.message || 'Batch submission is unconfirmed. Check its status; do not start another send.';
            }
        } finally { preparing = false; }
    }

    async function sendSms() {
        if (isSending || preparing || submissionUnconfirmed) return;
        const selected = selectedRecipients(getBaseListForFilter(), selectedIds);
        if (!selected.length) { errorMessage = 'Please select at least one recipient'; return; }
        if (!smsMessage.trim()) { errorMessage = 'SMS message is required'; return; }
        preparedPayload = Object.freeze({
            batch_id: crypto.randomUUID(), user_ids: selected.map(recipient => recipient.id),
            recipient_kind: selected[0]._kind, phone_target: phoneTarget,
            message: smsMessage.trim(), custom_phone: customPhone.trim() || null,
        });
        await submitPreparedBatch(preparedPayload);
    }

    function viewBatch(id) {
        if (isSending || preparing) return;
        clearTimeout(pollTimer); statusAbort?.abort(); statusGeneration++;
        batch = null; batchId = id; preparedPayload = null; submissionUnconfirmed = !!id;
        if (!id) { localStorage.removeItem(savedBatchKey); return; }
        localStorage.setItem(savedBatchKey, id); checkBatchStatus();
    }

    onDestroy(() => {
        alive = false; clearTimeout(pollTimer); statusAbort?.abort(); statusGeneration++;
    });
    // Reactive filtering
    $: searchQuery, activeFilter, phoneTarget, users, parents, thisMonthParents, inactiveMonthParents, inactiveMonthParentsGrade12OrBelow, applyFilters();
</script>

<div class="select-users-sms-container">
    <h3>Send SMS to Selected Recipients</h3>

    <div class="filter-buttons">
        <button class="filter-btn {activeFilter === 'active_date_students' ? 'active' : ''}" on:click={() => setFilter('active_date_students')}>
            Active Students by Date ({activeFilter === 'active_date_students' ? dateStudents.length : 0})
        </button>
        <button class="filter-btn {activeFilter === 'inactive_date_students' ? 'active' : ''}" on:click={() => setFilter('inactive_date_students')}>
            Inactive Students by Date ({activeFilter === 'inactive_date_students' ? dateStudents.length : 0})
        </button>
        <button class="filter-btn {activeFilter === 'all' ? 'active' : ''}" on:click={() => setFilter('all')}>
            All Users ({users.length})
        </button>
        <button class="filter-btn {activeFilter === 'students' ? 'active' : ''}" on:click={() => setFilter('students')}>
            Students ({users.filter(u => u.user_type === 'student').length})
        </button>
        <button class="filter-btn {activeFilter === 'teachers' ? 'active' : ''}" on:click={() => setFilter('teachers')}>
            Teachers ({users.filter(u => u.user_type === 'teacher').length})
        </button>
        <button class="filter-btn {activeFilter === 'parents' ? 'active' : ''}" on:click={() => setFilter('parents')}>
            Parents ({parents.length})
        </button>
        <button class="filter-btn {activeFilter === 'this_month_parents' ? 'active' : ''}" on:click={() => setFilter('this_month_parents')}>
            This Month Parents ({thisMonthParents.length})
        </button>
        <button class="filter-btn {activeFilter === 'inactive_month_parents' ? 'active' : ''}" on:click={() => setFilter('inactive_month_parents')}>
            Inactive This Month Parents ({inactiveMonthParents.length})
        </button>
        <button class="filter-btn {activeFilter === 'inactive_month_parents_grade_12_or_below' ? 'active' : ''}" on:click={() => setFilter('inactive_month_parents_grade_12_or_below')}>
            Inactive Parents Grade 12 or Below ({inactiveMonthParentsGrade12OrBelow.length})
        </button>
    </div>

    <div class="form-group">
        <label for="sms-phone-target">Send to</label>
        <select id="sms-phone-target" class="form-input" bind:value={phoneTarget} on:change={clearAll} disabled={isSending}>
            <option value="student">{activeFilter === 'teachers' ? 'Teacher phone' : 'Student phone'}</option>
            <option value="parent">Parent phone</option>
        </select>
        <p>Only the selected phone is used. Missing or failed numbers are listed below. Repeated requests and shared numbers are protected against duplicate sends.</p>
    </div>

    {#if activeFilter === 'active_date_students' || activeFilter === 'inactive_date_students'}
        {#key activeFilter}
            <ActiveStudentDateFilter channel="sms" inactive={activeFilter === 'inactive_date_students'} on:recipients={updateDateRecipients} />
        {/key}
    {/if}

    <div class="search-container">
        <input
            type="text"
            placeholder="Search by name, email, username, or phone..."
            bind:value={searchQuery}
            class="search-input"
        />
    </div>

    <div class="selection-controls">
        <button on:click={selectAllVisible} class="selection-btn">
            Select All Visible ({filtered.length})
        </button>
        <button on:click={clearAll} class="selection-btn">
            Clear All
        </button>
        <span class="selected-count">Selected: {selectedIds.size}</span>
    </div>

    {#if isLoading}
        <div class="loading">Loading recipients...</div>
    {:else if filtered.length === 0}
        <div class="no-users">No recipients found</div>
    {:else}
        <div class="users-list">
            {#each filtered as r (r.id)}
                <div class="user-item {selectedIds.has(r.id) ? 'selected' : ''}">
                    <label class="user-label">
                        <input
                            type="checkbox"
                            checked={selectedIds.has(r.id)}
                            on:change={() => toggleSelection(r.id)}
                        />
                        <div class="user-info">
                            <div class="user-name">
                                {recipientLabel(r)}
                                <span class="user-type {r.user_type}">{r.user_type}</span>
                            </div>
                            <div class="user-meta">
                                {#if r.phone_number}
                                    <span class="phone">Phone: {maskPhone(r.phone_number)}</span>
                                {:else}
                                    <span class="phone missing">No phone</span>
                                {/if}

                            </div>
                        </div>
                    </label>
                </div>
            {/each}
        </div>
    {/if}

    {#if selectedIds.size > 0}
        <div class="sms-form">
            <h4>SMS Details</h4>

            <div class="form-group">
                <label for="custom-phone">Custom phone (optional override):</label>
                <input
                    id="custom-phone"
                    type="text"
                    placeholder="e.g. +370..."
                    bind:value={customPhone}
                    disabled={isSending || preparing || submissionUnconfirmed}
                    class="form-input"
                />
            </div>

            <div class="form-group">
                <label for="sms-message">Message:</label>
                <textarea
                    id="sms-message"
                    bind:value={smsMessage}
                    disabled={isSending || preparing || submissionUnconfirmed}
                    placeholder="Enter SMS message..."
                    rows="6"
                    class="form-textarea"
                ></textarea>
            </div>

            <button on:click={sendSms} disabled={isSending || preparing || submissionUnconfirmed || selectedIds.size === 0} class="send-btn {isSending ? 'sending' : ''}">
                {#if isSending}Sending...{:else}Send SMS{/if}
            </button>
        </div>
    {/if}

    {#if recentBatches.length}
        <label for="recent-sms-batch">Recent SMS batches</label>
        <select id="recent-sms-batch" value={batchId} disabled={preparing || isSending} on:change={event => viewBatch(event.currentTarget.value)} class="form-input">
            <option value="">Select a batch</option>
            {#each recentBatches as item (item.batch_id)}
                <option value={item.batch_id}>{new Date(item.created_at).toLocaleString()} · {item.phone_target} · {item.message_preview}</option>
            {/each}
        </select>
    {/if}
    {#if batchId}
        <button type="button" on:click={checkBatchStatus} disabled={preparing}>Check batch status</button>
    {/if}
    {#if preparedPayload && submissionUnconfirmed}
        <button type="button" on:click={() => submitPreparedBatch(preparedPayload)} disabled={preparing}>Resume same batch</button>
    {/if}
    {#if batch}
        <section class="progress-container" aria-label="SMS batch results" aria-busy={isSending}>
            <h4>SMS batch · {batch.phone_target} phone</h4>
            <p>Recipients: {batch.total_users}. Awaiting attempt: {batch.pending_sends}.</p>
            <p>Duplicate sends prevented: {batch.duplicate_sends_skipped}.</p>
            <p class="success-message">{successMessage}</p>
            <p>Failed recipients: {batch.failed_sends}</p>
            {#if batch.monitoring}<p role="status">Accepted is not confirmed delivery. This list updates as Twilio reports results, including later failures.</p>{/if}
            {#if batch.failures.length}
                <div class="failed-summary">
                    <h4>Failed SMS recipients</h4>
                    <ul class="failed-list">{#each batch.failures as row (`${row.recipient_kind}-${row.recipient_id}`)}
                        <li>{row.label}{row.phone_last4 ? ` · phone ending ${row.phone_last4}` : ''}: {row.reason}</li>
                    {/each}</ul>
                </div>
            {/if}
            {#if batch.unconfirmed.length}
                <div class="unconfirmed-summary">
                    <h4>Unconfirmed sends — review before retrying</h4>
                    <ul>{#each batch.unconfirmed as row (`${row.recipient_kind}-${row.recipient_id}`)}<li>{row.label}: {row.reason}</li>{/each}</ul>
                </div>
            {/if}
        </section>
    {/if}
    {#if errorMessage}<div class="error-message" role="alert">{errorMessage}</div>{/if}
</div>

<style>



    .select-users-sms-container {
        max-width: 800px;
        margin: 0 auto;
        padding: 20px;
    }

    .filter-buttons {
        display: flex;
        gap: 10px;
        margin-bottom: 20px;
        flex-wrap: wrap;
    }

    .filter-btn {
        padding: 8px 16px;
        border: 1px solid #ddd;
        background: white;
        cursor: pointer;
        border-radius: 6px;
        font-size: 14px;
        transition: all 0.2s ease;
    }

    .filter-btn.active {
        background: #fab45a;
        color: black;
        border-color: #fab45a;
    }

    .search-container {
        margin-bottom: 15px;
    }

    .search-input {
        width: 100%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 14px;
    }

    .selection-controls {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 20px;
        flex-wrap: wrap;
    }

    .selection-btn {
        padding: 6px 12px;
        border: 1px solid #007bff;
        background: white;
        color: #007bff;
        cursor: pointer;
        border-radius: 4px;
        font-size: 13px;
        transition: all 0.2s ease;
    }

    .selected-count {
        font-weight: bold;
        color: #555;
        margin-left: auto;
    }

    .loading, .no-users {
        text-align: center;
        padding: 20px;
        color: #666;
        font-style: italic;
    }

    .users-list {
        max-height: 400px;
        overflow-y: auto;
        border: 1px solid #ddd;
        border-radius: 6px;
        margin-bottom: 20px;
    }

    .user-item {
        border-bottom: 1px solid #eee;
    }

    .user-item.selected {
        background-color: #f0f8ff;
    }

    .user-label {
        display: flex;
        align-items: center;
        padding: 12px;
        cursor: pointer;
        gap: 12px;
    }

    .user-info {
        flex: 1;
    }

    .user-name {
        font-weight: 500;
        margin-bottom: 4px;
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .user-type {
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
    }

    .user-type.student {
        background-color: #e3f2fd;
        color: #1976d2;
    }

    .user-type.teacher {
        background-color: #f3e5f5;
        color: #7b1fa2;
    }

    .user-type.parent {
        background-color: #fff3cd;
        color: #856404;
    }

    .user-meta {
        color: #666;
        font-size: 12px;
    }

    .phone.missing {
        color: #b00020;
        font-style: italic;
    }

    .sms-form {
        background: #f9f9f9;
        padding: 20px;
        border-radius: 8px;
        margin-top: 20px;
    }

    .form-group {
        margin-bottom: 15px;
    }

    .form-group label {
        display: block;
        margin-bottom: 5px;
        font-weight: 500;
        color: #333;
    }

    .form-input, .form-textarea {
        width: 100%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 14px;
        font-family: inherit;
    }

    .send-btn {
        padding: 12px 24px;
        background: #28a745;
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .send-btn:disabled {
        background: #6c757d;
        cursor: not-allowed;
    }

    .send-btn.sending {
        background: #ffc107;
        color: #212529;
    }

    .error-message {
        background: #f8d7da;
        color: #721c24;
        padding: 12px;
        border-radius: 6px;
        margin-top: 15px;
        border: 1px solid #f5c6cb;
    }

    .success-message {
        background: #d4edda;
        color: #155724;
        padding: 12px;
        border-radius: 6px;
        margin-top: 15px;
        border: 1px solid #c3e6cb;
    }

    .progress-container {
        margin-top: 20px;
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .progress-header {
        background: #f8f9fa;
        color: #333;
        padding: 12px 15px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #e0e0e0;
    }

    .progress-header h4 {
        margin: 0;
        font-size: 14px;
        font-weight: 500;
        color: #333;
    }

    .close-progress {
        background: none;
        border: none;
        color: #666;
        font-size: 18px;
        cursor: pointer;
        padding: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 3px;
    }

    .progress-logs {
        height: 250px;
        overflow-y: auto;
        padding: 15px;
        background: white;
        font-size: 13px;
        line-height: 1.5;
    }

    .success-entry {
        margin-bottom: 8px;
        display: flex;
        align-items: flex-start;
        word-wrap: break-word;
        padding: 4px 0;
    }

    .timestamp {
        color: #6c757d;
        margin-right: 10px;
        flex-shrink: 0;
        font-size: 11px;
        font-family: monospace;
    }

    .success-message {
        color: #28a745;
        font-weight: 500;
        flex: 1;
        background: none;
        border: none;
        padding: 0;
        margin: 0;
    }

    .completion-message {
        margin-top: 15px;
        padding: 10px;
        background: #d4edda;
        color: #155724;
        border-radius: 4px;
        font-weight: 500;
        text-align: center;
    }

    .failed-summary {
        margin-top: 20px;
        background: #fff3cd;
        border: 1px solid #ffeaa7;
        border-radius: 8px;
        padding: 20px;
        color: #856404;
    }

    .failed-list {
        background: white;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        padding: 15px;
        margin: 15px 0 0 0;
        max-height: 200px;
        overflow-y: auto;
    }
</style>

